<template>
    <li
        ref="root"
        class="dropdown notifications-menu"
        :class="{ open: menuOpen }"
    >
        <a
            href="#"
            class="dropdown-toggle"
            :aria-label="infoMessage"
            @click.stop.prevent="toggleMenu"
        >
            <i class="fa fa-bell"></i>
            <span
                class="topbar-notifications-bell__badge"
                :class="{ 'topbar-notifications-bell__badge--capped': unreadCount > 9 }"
                v-show="unreadCount > 0"
                aria-hidden="true"
            >{{ badgeLabel }}</span>
        </a>
        <ul class="dropdown-menu">
            <li class="header">{{ infoMessage }}</li>
            <li>
                <ul class="menu">
                    <topbar-unread-notification
                        v-for="unreadNotification in unreadNotifications"
                        :unread-notification="unreadNotification"
                        :key="'in-app-' + unreadNotification.id"
                    />
                    <li class="text-center" v-if="unreadNotifications.length === 0">
                        <a href="#">{{ translate('No new notifications') }}</a>
                    </li>
                </ul>
            </li>
            <li class="footer topbar-notifications-bell__footer">
                <a
                    href="#"
                    class="topbar-notifications-bell__mark-all"
                    v-show="unreadCount > 0"
                    @click.stop.prevent="markAllRead"
                >{{ translate('Mark all as read') }}</a>
                <a :href="viewAllRoute">{{ translate('View all') }}</a>
            </li>
        </ul>
    </li>
</template>

<script>
import { EventBus } from '@admin-panel/eventBus';
import TopbarUnreadNotification from './unreadNotification.vue';

const SOUND_SRC = '/audio/user-notifications/notify.wav';

/**
 * Single topbar island: root is AdminLTE `li` so `li > a` and `li.open > .dropdown-menu` match.
 */
export default {
    name: 'TopbarNotificationsBell',
    components: {
        'topbar-unread-notification': TopbarUnreadNotification,
    },
    props: {
        initialUnreadCount: { type: [Number, String], default: 0 },
        getRoute: { type: String, required: true },
        viewAllRoute: { type: String, required: true },
        markAllReadRoute: { type: String, required: true },
    },
    data() {
        return {
            menuOpen: false,
            unreadNotifications: [],
            unreadCount: 0,
            infoMessage: '',
            pollInterval: null,
            echoChannelName: null,
            fetchGeneration: 0,
            onNotificationRead: null,
            onDocumentClick: null,
            onVisibilityChange: null,
            onInAppNotificationCreated: null,
            onInAppNotificationRead: null,
            onEchoStateChange: null,
            lastVisibilityResyncAt: 0,
            notifyAudio: null,
            markAllBusy: false,
        };
    },
    computed: {
        badgeLabel() {
            return this.unreadCount > 9 ? '9+' : String(this.unreadCount);
        },
    },
    created() {
        this.unreadCount = this.coerceCount(this.initialUnreadCount);
        this.infoMessage = this.buildInfoMessage(this.unreadCount);

        this.onNotificationRead = ({ unreadNotificationsCount, notificationId, infoMessage }) => {
            if (notificationId != null) {
                const i = this.unreadNotifications.map((item) => item.id).indexOf(notificationId);
                if (i !== -1) {
                    this.unreadNotifications.splice(i, 1);
                }
            }
            if (typeof unreadNotificationsCount === 'number') {
                this.unreadCount = unreadNotificationsCount;
                this.infoMessage = this.buildInfoMessage(unreadNotificationsCount);
            } else if (infoMessage) {
                this.infoMessage = infoMessage;
            } else {
                this.infoMessage = this.buildInfoMessage(this.unreadNotifications.length);
            }
        };
        EventBus.on('notificationRead', this.onNotificationRead);

        this.fetchUnread();
        this.subscribeEcho();
        this.prepareSound();
    },
    mounted() {
        this.onDocumentClick = (event) => {
            if (!this.menuOpen) {
                return;
            }
            const root = this.$refs.root;
            if (root && !root.contains(event.target)) {
                this.menuOpen = false;
            }
        };
        document.addEventListener('click', this.onDocumentClick);

        this.onVisibilityChange = () => {
            if (document.visibilityState !== 'visible') {
                return;
            }
            const now = Date.now();
            if (now - this.lastVisibilityResyncAt < 5000) {
                return;
            }
            this.lastVisibilityResyncAt = now;
            this.fetchUnread();
        };
        document.addEventListener('visibilitychange', this.onVisibilityChange);
    },
    beforeUnmount() {
        EventBus.off('notificationRead', this.onNotificationRead);
        this.unsubscribeEcho();
        if (this.pollInterval) {
            clearInterval(this.pollInterval);
        }
        if (this.onDocumentClick) {
            document.removeEventListener('click', this.onDocumentClick);
        }
        if (this.onVisibilityChange) {
            document.removeEventListener('visibilitychange', this.onVisibilityChange);
        }
        this.menuOpen = false;
    },
    methods: {
        coerceCount(value) {
            const n = Number(value);
            return Number.isFinite(n) && n > 0 ? n : 0;
        },
        toggleMenu() {
            this.menuOpen = !this.menuOpen;
        },
        markAllRead() {
            if (!this.markAllReadRoute || this.markAllBusy || this.unreadCount === 0) {
                return;
            }

            this.markAllBusy = true;
            axios.post(this.markAllReadRoute).then((res) => {
                this.unreadNotifications = [];
                const count = typeof res.data.unreadNotificationsCount === 'number'
                    ? res.data.unreadNotificationsCount
                    : 0;
                this.publishUnreadCount(count);
            }).catch(() => {}).finally(() => {
                this.markAllBusy = false;
            });
        },
        translate(key, args) {
            const fn = (typeof this._t === 'function' && this._t)
                || (typeof window._t === 'function' && window._t)
                || null;
            if (fn) {
                try {
                    return fn(key, args);
                } catch (_) {
                    // fall through
                }
            }
            if (args && args.num !== undefined) {
                return key.replace(':num', String(args.num));
            }
            return key;
        },
        buildInfoMessage(count) {
            return this.translate('You have :num new notifications', { num: count });
        },
        prepareSound() {
            try {
                this.notifyAudio = new Audio(SOUND_SRC);
                this.notifyAudio.preload = 'auto';
            } catch (_) {
                this.notifyAudio = null;
            }
        },
        playNotifySound() {
            if (!this.notifyAudio) {
                return;
            }
            try {
                this.notifyAudio.currentTime = 0;
                const playPromise = this.notifyAudio.play();
                if (playPromise && typeof playPromise.catch === 'function') {
                    playPromise.catch(() => {});
                }
            } catch (_) {
                // ignore
            }
        },
        subscribeEcho() {
            const userId = window.Laravel?.userId;
            if (window.Echo && userId) {
                this.echoChannelName = `App.User.${userId}`;

                this.onInAppNotificationCreated = (payload) => {
                    this.prependInAppNotification(payload);
                    this.playNotifySound();
                };
                this.onInAppNotificationRead = (payload) => {
                    this.applyInAppNotificationsRead(payload);
                };

                window.Echo.private(this.echoChannelName)
                    .listen('.in-app-notification.created', this.onInAppNotificationCreated)
                    .listen('.in-app-notification.read', this.onInAppNotificationRead);

                this.bindEchoReconnect();
                return;
            }
            if (this.getRoute) {
                this.pollInterval = setInterval(() => this.fetchUnread(), 60000);
            }
        },
        bindEchoReconnect() {
            const connection = window.Echo?.connector?.pusher?.connection;
            if (!connection) {
                return;
            }

            this.onEchoStateChange = (states) => {
                if (states?.current === 'connected' && states?.previous !== 'connected') {
                    this.fetchUnread();
                }
            };
            connection.bind('state_change', this.onEchoStateChange);
        },
        unsubscribeEcho() {
            if (!window.Echo || !this.echoChannelName) {
                return;
            }

            const channel = window.Echo.private(this.echoChannelName);
            if (this.onInAppNotificationCreated) {
                channel.stopListening('.in-app-notification.created', this.onInAppNotificationCreated);
            }
            if (this.onInAppNotificationRead) {
                channel.stopListening('.in-app-notification.read', this.onInAppNotificationRead);
            }

            const connection = window.Echo?.connector?.pusher?.connection;
            if (connection && this.onEchoStateChange) {
                connection.unbind('state_change', this.onEchoStateChange);
            }
        },
        applyInAppNotificationsRead(payload) {
            if (!payload || typeof payload.unread_count !== 'number') {
                return;
            }

            const ids = payload.notification_ids;
            if (ids === null) {
                this.unreadNotifications = [];
            } else if (Array.isArray(ids) && ids.length > 0) {
                const idSet = new Set(ids.map((id) => Number(id)));
                this.unreadNotifications = this.unreadNotifications.filter(
                    (item) => !idSet.has(Number(item.id)),
                );
            }

            this.publishUnreadCount(payload.unread_count);
        },
        fetchUnread() {
            if (!this.getRoute) {
                return;
            }

            const generation = ++this.fetchGeneration;

            axios.get(this.getRoute).then((res) => {
                if (generation !== this.fetchGeneration) {
                    return;
                }

                const serverItems = (res.data.unreadNotifications || [])
                    .map((item) => this.normalizeInAppNotification(item));
                const serverIds = new Set(serverItems.map((item) => item.id));

                const echoOnly = this.unreadNotifications.filter(
                    (item) => item.fromEcho && !serverIds.has(item.id),
                );

                this.unreadNotifications = [...echoOnly, ...serverItems];

                const serverCount = typeof res.data.unreadNotificationsCount === 'number'
                    ? res.data.unreadNotificationsCount
                    : serverItems.length;
                const count = Math.max(serverCount, this.unreadNotifications.length);

                this.publishUnreadCount(count);
            }).catch(() => {});
        },
        prependInAppNotification(payload) {
            const normalized = this.normalizeInAppNotification(payload, true);

            if (this.unreadNotifications.some((item) => item.id === normalized.id)) {
                return;
            }

            this.unreadNotifications.unshift(normalized);
            this.publishUnreadCount(this.unreadNotifications.length);
        },
        publishUnreadCount(count) {
            const safeCount = typeof count === 'number' && count >= 0 ? count : 0;
            this.unreadCount = safeCount;
            this.infoMessage = this.buildInfoMessage(safeCount);
            EventBus.emit('notificationRead', {
                unreadNotificationsCount: safeCount,
                notificationId: null,
            });
        },
        normalizeInAppNotification(notification, fromEcho = false) {
            const showUrl = notification.view_route
                || (notification.id ? `/in-app-notifications/${notification.id}` : null)
                || '#';

            return {
                id: notification.id,
                title: notification.title,
                view_route: showUrl,
                in_app: true,
                fromEcho: Boolean(fromEcho),
            };
        },
    },
};
</script>

<style>
/*
 * Social / Material-style unread badge on dark AdminLTE navbar:
 * - no Bootstrap `.label` (asymmetric padding → fat pill + white ghost)
 * - ~16px circle (M3 large one-digit), slight pill for "9+"
 * - cutout via box-shadow in navbar color (not white — white reads as a tail on dark)
 * - red = universal unread signal (Instagram / iOS / Material error)
 */
.navbar-nav > .notifications-menu > a {
    position: relative;
}

.navbar-nav > .notifications-menu > a > .topbar-notifications-bell__badge {
    position: absolute;
    top: 7px;
    right: 4px;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    border: 0;
    border-radius: 999px;
    background-color: #ef4444;
    color: #fff;
    font-size: 9px;
    font-weight: 700;
    font-style: normal;
    line-height: 1;
    letter-spacing: 0;
    text-align: center;
    text-indent: 0;
    white-space: nowrap;
    pointer-events: none;
    /* AdminLTE dark navbar (#222d32 / skin-black ~#1a2226) */
    box-shadow: 0 0 0 2px #1a2226;
    z-index: 1;
}

.navbar-nav > .notifications-menu > a > .topbar-notifications-bell__badge--capped {
    min-width: 20px;
    padding: 0 5px 0 4px;
}

.notifications-menu > .dropdown-menu > .topbar-notifications-bell__footer {
    display: flex;
    align-items: stretch;
    justify-content: space-between;
    padding: 0;
}

.notifications-menu > .dropdown-menu > .topbar-notifications-bell__footer > a {
    flex: 1 1 50%;
    display: block;
    text-align: center;
    white-space: nowrap;
}
</style>
